import {Fill, Stroke, Circle, Style} from 'ol/style';
import 'ol/ol.css';
import GeoJSON from 'ol/format/GeoJSON';
import Map from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {fromLonLat} from 'ol/proj';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import http from 'http';
import Overlay from 'ol/Overlay';

const STYLES = {
  'POINT_DARK_BLUE': 'point_dark_blue',
  'POLYGON_DARK_BLUE': 'polygon_dark_blue',
  'POLYGON_SEMI_TRANSPARENT': 'polygon_semi_transparent'
};

/**
 * Available configuration objects
 * The configuration object that is loaded is the one matching the URL hash.
 * Thus for http://localhost:3000/#kiosk, the object with the 'kiosk' key is the one that contains the configuration that is going to be loaded.
 */
var availableLayers = {
  'kiosk': {
    'name': 'Kiosque',
    'dataUrl': './data/kiosques-de-presse.geojson',
    'style': STYLES.POINT_DARK_BLUE,
    'pointRadius': 2,
    'popupContent': {
      'title': ['code_mobilier'],
      'values': {
        'adresse': 'Adresse',
        'code_postal': 'Code postal',
        'type_activite': 'Activité',
        'geo_point_2d': 'Longitude et latitude'
      }
    }
  },
  'velib': {
    'name': 'Station Velib',
    'dataUrl': './data/velib-disponibilite-en-temps-reel.geojson',
    'style': STYLES.POINT_DARK_BLUE,
    'pointRadius': 2,
    'popupContent': {
      'title': ['stationcode'],
      'values': {
        'name': 'Nom',
        'is_installed': 'Station en service',
        'is_renting': 'Location possible',
        'is_returning': 'Dépôt possible',
        'ebike': 'Nombre de vélos électriques',
        'coordonnees_geo': 'Longitude et latitude'
      }
    }
  },
  'stroll': {
    'name': 'Promenade',
    'dataUrl': './data/paris-autrement-balades-dans-les-arrondissements-peripheriques-poi.geojson',
    'style': STYLES.POINT_DARK_BLUE,
    'pointRadius': 2,
    'popupContent': {
      'title': ['nom_poi'],
      'values': {
        'adresse': 'Adresse',
        'code_postal': 'Code postal',
        'ville': 'Ville',
        'texte_description': 'Description',
        'geo_point_2d': 'Longitude et latitude'
      }
    }
  },
  'film': {
    'name': 'Film',
    'dataUrl': './data/tournagesdefilmsparis2011.geojson',
    'style': STYLES.POINT_DARK_BLUE,
    'pointRadius': 2,
    'popupContent': {
      'title': ['titre'],
      'values': {
        'adresse': 'Adresse',
        'ardt': 'Arrondissement',
        'date_debut': 'Date de début',
        'date_fin': 'Date de fin',
        'realisateur': 'Réalisateur',
        'type_de_tournage': 'Type de tournage',
        'organisme_demandeur': 'Organisme demandeur'
      }
    }
  },
  'construction': {
    'name': 'Période de construction',
    'dataUrl': './data/EMPRISE_BATIE_PARIS.geojson',
    'style': STYLES.POLYGON_DARK_BLUE,
    'strokeWidth': 1,
    'popupContent': {
      'title': ['c_perconst'],
      'values': {
        'OBJECTID': 'OBJECTID',
        'Shape_Area': 'Shape_Area',
        'Shape_Length': 'Shape_Length',
        'an_const': 'an_const',
        'an_rehab': 'an_rehab',
        'an_rehabf': 'an_rehabf',
        'b_dalle': 'b_dalle',
        'b_ee': 'b_ee',
        'b_er': 'b_er',
        'b_horspu': 'b_horspu',
        'b_igh': 'b_igh',
        'c_ens_moy': 'c_ens_moy',
        'c_morpho': 'c_morpho',
        'c_perconst': 'c_perconst',
        'c_source': 'c_source',
        'c_toitdom': 'c_toitdom',
        'h_max': 'h_max',
        'h_med': 'h_med',
        'h_min': 'h_min',
        'h_moy': 'h_moy',
        'm2_ardoise': 'm2_ardoise',
        'm2_beton': 'm2_beton',
        'm2_e_70_80': 'm2_e_70_80',
        'm2_e_80_90': 'm2_e_80_90',
        'm2_e_90_10': 'm2_e_90_10',
        'm2_e_in700': 'm2_e_in700',
        'm2_e_s1000': 'm2_e_s1000',
        'm2_e_tot': 'm2_e_tot',
        'm2_terrveg': 'm2_terrveg',
        'm2_tuile': 'm2_tuile',
        'm2_zinc': 'm2_zinc',
        'n_sq_ar': 'n_sq_ar',
        'n_sq_eb': 'n_sq_eb',
        'n_sq_pc': 'n_sq_pc',
        'n_sq_pu': 'n_sq_pu'
      }
    }
  },
  'terrace': {
    'name': 'Terrasse',
    'dataUrl': './data/etalages-et-terrasses.geojson',
    'style': STYLES.POLYGON_DARK_BLUE,
    'filters': ['TERRASSE OUVERTE', 'TERRASSE FERMEE'],
    'popupContent': {
      'title': ['lieu1'],
      'values': {
        'libelle_type': 'Type',
        'largeurmin': 'Largeur minimale',
        'largeurmax': 'Largeur maximale',
        'longueur': 'Longueur',
        'affichette': 'Affichette',
        'geo_point_2d': 'Longitude et latitude'
      }
    }
  },
  'district': {
    'name': 'Arrondissement',
    'dataUrl': './data/arrondissements.geojson',
    'style': STYLES.POLYGON_SEMI_TRANSPARENT,
    'strokeWidth': 1,
    'zindex': 0
  }
};

/**
 * The OpenLayer map
 */
var map = null;

/**
 * Elements that make up the popup.
 */
var container = document.getElementById('popup');
var content = document.getElementById('popup-content');
var closer = document.getElementById('popup-closer');

/**
 * The configuration object loaded based on the URL hash
 */
var configurationLoaded = null;

function setStyle(style, strokeWidth = 3, pointRadius = 2) {

  var result = null;

  switch (style) {
    case STYLES.POINT_DARK_BLUE:
      result = new Style({
        image: new Circle({
          fill: new Fill({color: 'rgba(11, 64, 139, 1)', weight: 1}),
          stroke: new Stroke({color: 'rgba(11, 64, 139, 1)', width: strokeWidth}),
          radius: pointRadius
        }),
        fill: new Fill({color: 'rgba(11, 64, 139, 1)', weight: 1}),
        stroke: new Stroke({color: 'rgba(11, 64, 139, 1)', width: strokeWidth})
      });
      break;
    case STYLES.POLYGON_DARK_BLUE:
      result = new Style({
        fill: new Fill({color: 'rgba(11, 64, 139, 1)', weight: 1}),
        stroke: new Stroke({color: 'rgba(11, 64, 139, 1)', width: strokeWidth})
      });
      break;
    case STYLES.POLYGON_SEMI_TRANSPARENT:
      result = new Style({
        fill: new Fill({color: 'rgba(255, 255, 255, 0.5)', weight: 1}),
        stroke: new Stroke({color: 'red', width: strokeWidth})
      });
      break;
    default:
      break;
  }

  return result;
}

function getJson(url, callback, configuration) {
  http.get(url, function(res) {
    var body = '';
    res.on('data', function(chunk) {
      body += chunk;
    });

    res.on('end', function() {
      var response = JSON.parse(body);
      // call function ----v
      callback(response, configuration);
    });
  });
}

function createOverlay() {
  /**
 * Create an overlay to anchor the popup to the map.
 */
  var overlay = new Overlay({
    element: container,
    autoPan: true,
    autoPanAnimation: {
      duration: 250
    }
  });
  return overlay;
}

function createMap() {
  map = new Map({
    target: 'map-container',
    layers: [],
    view: new View({
      center: fromLonLat([2.349014, 48.864716]),
      zoom: 12.8
    })
  });
}

function createVectorSource({dataUrl = null, geoJsonOptions = null, preloadedData = null}) {
  var vectorSource = null;
  if (geoJsonOptions !== null && preloadedData !== null) {
    vectorSource = new VectorSource({
      features: (new GeoJSON(geoJsonOptions)).readFeatures(preloadedData)
    });
  } else {
    vectorSource = new VectorSource({
      format: new GeoJSON(),
      url: dataUrl
    });
  }
  return vectorSource;
}

function createVectorLayer(vectorSource, style, strokeWidth = 3, pointRadius, zindex = 10) {
  var vectorLayer = new VectorLayer({
    source: vectorSource,
    style: setStyle(style, strokeWidth, pointRadius),
    zIndex: zindex
  });

  return vectorLayer;
}

function disableLoadingSpinner(map) {
  var vectorSourcesLoaded = 0;
  var totalVectorSources = 0;

  map.getLayers().forEach(layer=>{
    var vs = layer.getSource();
    if (vs instanceof VectorSource) {
      totalVectorSources++;
    }
  });

  map.getLayers().forEach(layer=>{
    var vs = layer.getSource();
    var listenerKey = vs.on('change', function(e) {
      if (vs.getState() == 'ready') {
        vectorSourcesLoaded++;
        vs.un(listenerKey);

        if (vectorSourcesLoaded == totalVectorSources) {
          document.getElementById('loading-container').className = 'hide';
        }
      }
    });
  });
}


function createFilteredVectorLayer(fullGeoJSON, configuration) {
  var vectorSource = null;
  var vectorLayer = null;

  var filteredGeoJSON = {
    'type': 'FeatureCollection',
    'features': []
  };

  fullGeoJSON.features.forEach(function(feature) {
    configuration.filters.forEach(filter=>{
      if (feature.properties.libelle_type == filter) {
        filteredGeoJSON.features.push(feature);
      }
    });
  });

  vectorSource = createVectorSource({
    'geoJsonOptions': {defaultDataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857'},
    'preloadedData': filteredGeoJSON
  });

  vectorLayer = createVectorLayer(vectorSource, configuration.style, configuration.strokeWidth, configuration.pointRadius, configuration.zindex);

  map.addLayer(vectorLayer);
}

function loadMapLayers(configuration) {

  var keyFound = false;
  var osmVectorLayer = new TileLayer({
    source: new OSM()
  });
  map.addLayer(osmVectorLayer);

  var districtVectorSource = createVectorSource({'dataUrl': configuration.district.dataUrl});
  var districtVectorLayer = createVectorLayer(districtVectorSource, configuration.district.style, configuration.district.strokeWidth, configuration.district.zindex);
  map.addLayer(districtVectorLayer);

  var vl = null;
  for (const [key, value] of Object.entries(configuration)) {

    // Only load the layer requested via the URL hash.
    // There can be only one hash in the URL.
    var hash = window.location.hash.substring(1);
    if (hash == key) {
      keyFound = true;

      configurationLoaded = value;

      // The GeoJson has to filtered on the client side.
      // Retrieve the data, filter them, create a layer based on the filtered version and add them to the map.
      if (value.hasOwnProperty('filters')) {
        getJson(value.dataUrl, createFilteredVectorLayer, value);
      } else {
        var vectorSource = createVectorSource({'dataUrl': value.dataUrl});
        vl = createVectorLayer(vectorSource, value.style, value.strokeWidth, value.pointRadius, value.zindex);
        map.addLayer(vl);
        break;
      }
    }
  }

  if (keyFound === false) {
    document.getElementById('loading-container').innerHTML = 'La carte demandée ne peut pas être intégralement chargée.';
  }

  disableLoadingSpinner(map);

}

function onMapSingleClick() {
  map.on('click', function(evt) {
    var feature = map.forEachFeatureAtPixel(evt.pixel,
      function(feature, layer) {
        return feature;
      });

    if (feature) {
      var geometry = feature.getGeometry();
      var coord = geometry.getCoordinates();

      if (coord === void 0 || coord.length == 0) {
        return;
      }

      if (Array.isArray(coord[0])) {
        coord = coord[0][0];
      }
      var ps = feature.getProperties();

      var overlay = map.getOverlays().item(0);
      var c = configurationLoaded;

      var title = '<h3>' + c.name;
      if (c.popupContent.title !== null) {
        title += ' ' + ps[c.popupContent.title];
      }
      title += '</h3>';

      var showContainer = false;
      var body = '';
      for (const [key, value] of Object.entries(c.popupContent.values)) {

        if (ps[key] !== void 0) {
          body += '<p>' + value + ': ' + ps[key] + '</p>';
          showContainer = true;
        } else {
          body += '<p>' + value + ': - </p>';
        }
      }

      if (showContainer) {
        content.innerHTML = title + body;
        overlay.setPosition(coord);
      }
    }
  });
}

function onClosePopup() {
  /**
 * Add a click handler to hide the popup.
 * @return {boolean} Don't follow the href.
 */
  closer.onclick = function() {
    var overlay = map.getOverlays().item(0);
    overlay.setPosition(undefined);
    closer.blur();
    return false;
  };
}

// To ensure compatibility with Chrome <5, Firefox <4.0, IE <9, Opera <10.5 and Safari <5
if (typeof Array.isArray === 'undefined') {
  Array.isArray = function(obj) {
    return Object.prototype.toString.call(obj) === '[object Array]';
  };
}

createMap();
loadMapLayers(availableLayers);

var overlay = createOverlay();
map.addOverlay(overlay);

onMapSingleClick();
onClosePopup();

